import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Route Editor Context
 *
 * React-first state management for BFT route editing.
 * This context owns all route state directly (no window.routeMap dependency)
 * and manages Leaflet layer rendering through effects.
 */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, } from 'react';
import { useMap } from './MapContext';
import { useDialog } from './DialogContext';
import { useRouteApi } from '../hooks/useRouteApi';
import { createDefaultMetadata, parseNois, isDuctFeature, } from '../types/route-editor';
// =============================================================================
// Context Definition
// =============================================================================
const RouteEditorContext = createContext(null);
export const useRouteEditor = () => {
    const context = useContext(RouteEditorContext);
    if (!context) {
        throw new Error('useRouteEditor must be used within RouteEditorProvider');
    }
    return context;
};
export const RouteEditorProvider = ({ children }) => {
    const { getMap, isMapReady } = useMap();
    const { confirm } = useDialog();
    const api = useRouteApi();
    // =========================================================================
    // Core State - React owns this, not window
    // =========================================================================
    const [ducts, setDucts] = useState(new Map());
    const [metadata, setMetadata] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [defaultStatus, setDefaultStatus] = useState('Installed');
    const [originalState, setOriginalState] = useState(null);
    // Leaflet layer reference
    const routeLayerRef = useRef(null);
    // =========================================================================
    // Computed Values
    // =========================================================================
    const lineSections = useMemo(() => {
        return Array.from(ducts.values()).filter((d) => d.geometry.type === 'LineString');
    }, [ducts]);
    const totalDistance = useMemo(() => {
        return lineSections.reduce((sum, duct) => sum + (duct.properties.MEASURED_LENGTH || 0), 0);
    }, [lineSections]);
    const routeInfo = useMemo(() => {
        const isNewRoute = !metadata?.id;
        return {
            name: metadata?.ourRef?.trim() || 'NEW ROUTE',
            sectionCount: lineSections.length,
            totalDistance,
            isNewRoute,
        };
    }, [metadata, lineSections.length, totalDistance]);
    // =========================================================================
    // Leaflet Layer Management
    // =========================================================================
    useEffect(() => {
        if (!isMapReady)
            return;
        const map = getMap();
        if (!map)
            return;
        const CFN = window.CFN;
        const L = window.L;
        // Remove existing layer
        if (routeLayerRef.current && map.hasLayer(routeLayerRef.current)) {
            map.removeLayer(routeLayerRef.current);
            routeLayerRef.current = null;
        }
        // Don't create layer if no ducts
        if (ducts.size === 0)
            return;
        // Create feature collection from ducts
        const features = Array.from(ducts.values());
        const featureCollection = {
            type: 'FeatureCollection',
            features,
        };
        // Create new GeoJSON layer with CFN styling
        routeLayerRef.current = L.geoJSON(featureCollection, {
            style: CFN?.PIAStyle || ((feature) => ({
                color: '#0066ff',
                weight: Math.min((feature.properties?.NUMBER_OF_WAYS || 2) + 1, 6),
            })),
            pointToLayer: CFN?.PIAIcons,
            onEachFeature: (feature, layer) => {
                if (CFN?.PIALineStyle) {
                    CFN.PIALineStyle(feature, layer, true);
                }
            },
        });
        // Add to map and bring to front
        if (routeLayerRef.current) {
            map.addLayer(routeLayerRef.current);
            routeLayerRef.current.bringToFront();
        }
        // Cleanup on unmount or when ducts change
        return () => {
            if (routeLayerRef.current && map.hasLayer(routeLayerRef.current)) {
                map.removeLayer(routeLayerRef.current);
            }
        };
    }, [ducts, isMapReady, getMap]);
    // Periodic bring-to-front to maintain z-order
    useEffect(() => {
        const interval = setInterval(() => {
            if (routeLayerRef.current && isMapReady) {
                const map = getMap();
                if (map && map.hasLayer(routeLayerRef.current)) {
                    routeLayerRef.current.bringToFront();
                }
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [isMapReady, getMap]);
    // =========================================================================
    // Duct Actions
    // =========================================================================
    const addDuct = useCallback((feature) => {
        const dbId = feature.properties.DB_ID;
        // Apply default status if not set
        if (!feature.properties.BFT_STATUS) {
            feature.properties.BFT_STATUS = defaultStatus;
        }
        // Set default NOI if available and not set
        if (!feature.properties.BFT_PIANOI && feature.nois?.length > 0) {
            feature.properties.BFT_PIANOI = feature.nois[0];
        }
        setDucts(prev => {
            const next = new Map(prev);
            next.set(dbId, feature);
            return next;
        });
        setIsDirty(true);
    }, [defaultStatus]);
    const removeDuct = useCallback((dbId) => {
        setDucts(prev => {
            const next = new Map(prev);
            next.delete(dbId);
            return next;
        });
        setIsDirty(true);
    }, []);
    const updateDuctStatus = useCallback((dbId, status) => {
        setDucts(prev => {
            const next = new Map(prev);
            const duct = next.get(dbId);
            if (duct) {
                next.set(dbId, {
                    ...duct,
                    properties: {
                        ...duct.properties,
                        BFT_STATUS: status,
                    },
                });
            }
            return next;
        });
        setIsDirty(true);
    }, []);
    const updateDuctNoi = useCallback((dbId, noi) => {
        setDucts(prev => {
            const next = new Map(prev);
            const duct = next.get(dbId);
            if (duct) {
                next.set(dbId, {
                    ...duct,
                    properties: {
                        ...duct.properties,
                        BFT_PIANOI: noi,
                    },
                });
            }
            return next;
        });
        setIsDirty(true);
    }, []);
    const clearDucts = useCallback(() => {
        setDucts(new Map());
        setIsDirty(true);
    }, []);
    // =========================================================================
    // Route Actions
    // =========================================================================
    const loadRoute = useCallback(async (bftRef) => {
        try {
            const data = await api.loadRoute(bftRef);
            const { bft, featureCollection } = data;
            // Clear existing ducts
            const newDucts = new Map();
            // Collect all coordinates for bounds calculation
            const allCoords = [];
            // Add features from loaded route
            featureCollection.features.forEach(feature => {
                if (isDuctFeature(feature)) {
                    // Ensure NOIs are parsed
                    if (!feature.nois) {
                        feature.nois = parseNois(feature.properties.ORREFERENCEIDS);
                    }
                    newDucts.set(feature.properties.DB_ID, feature);
                    // Collect coordinates for bounds (GeoJSON is [lng, lat], Leaflet needs [lat, lng])
                    if (feature.geometry.type === 'LineString') {
                        feature.geometry.coordinates.forEach(coord => {
                            allCoords.push([coord[1], coord[0]]); // Convert to [lat, lng]
                        });
                    }
                }
                else if (feature.geometry.type === 'Point') {
                    const coord = feature.geometry.coordinates;
                    allCoords.push([coord[1], coord[0]]); // Convert to [lat, lng]
                }
            });
            // Update state
            const newMetadata = {
                id: String(bft.id),
                ourRef: bft.our_ref,
                description: bft.description,
                cableType: bft.cableType,
                rfs: bft.rfs,
            };
            setDucts(newDucts);
            setMetadata(newMetadata);
            setIsDirty(false);
            // Store original state for reset
            setOriginalState({
                metadata: { ...newMetadata },
                ducts: new Map(newDucts),
            });
            // Fit map to route bounds using collected coordinates
            if (isMapReady && allCoords.length > 0) {
                const map = getMap();
                const L = window.L;
                if (map && L) {
                    const bounds = L.latLngBounds(allCoords);
                    if (bounds.isValid()) {
                        map.fitBounds(bounds, { padding: [50, 50] });
                    }
                }
            }
        }
        catch (error) {
            console.error('Failed to load route:', error);
            throw error;
        }
    }, [api, isMapReady, getMap]);
    const saveRoute = useCallback(async () => {
        if (!metadata) {
            return { success: false, error: 'No route metadata' };
        }
        // Validate required fields
        if (!metadata.ourRef?.trim()) {
            return { success: false, error: 'BFT Reference is required' };
        }
        if (!metadata.description?.trim()) {
            return { success: false, error: 'Description is required' };
        }
        const result = await api.saveRoute(metadata, ducts);
        if (result.success) {
            // Update metadata with new ID if created
            if (result.id && !metadata.id) {
                setMetadata(prev => prev ? { ...prev, id: String(result.id) } : null);
            }
            setIsDirty(false);
            // Update original state
            setOriginalState({
                metadata: { ...metadata, id: metadata.id || String(result.id) },
                ducts: new Map(ducts),
            });
        }
        return result;
    }, [metadata, ducts, api]);
    const resetRoute = useCallback(() => {
        const isNewRoute = !metadata?.id;
        if (isNewRoute) {
            // For new routes, clear everything
            setDucts(new Map());
            setMetadata(null);
            setIsDirty(false);
            setOriginalState(null);
        }
        else if (originalState) {
            // For existing routes, revert to original state
            setDucts(new Map(originalState.ducts));
            setMetadata({ ...originalState.metadata });
            setIsDirty(false);
        }
    }, [metadata, originalState]);
    const copyRoute = useCallback(() => {
        if (isDirty || !metadata?.id)
            return;
        setMetadata(prev => prev ? {
            ...prev,
            id: null,
            ourRef: '',
            description: `Copy of ${prev.description}`,
        } : null);
        setIsDirty(true);
        setOriginalState(null);
    }, [isDirty, metadata]);
    const deleteRoute = useCallback(async () => {
        if (!metadata?.id) {
            return { success: false, error: 'No route to delete' };
        }
        const confirmed = await confirm({
            title: 'Delete Route',
            description: `Are you sure you want to delete "${metadata.ourRef}"? This cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'destructive',
        });
        if (!confirmed) {
            return { success: false, error: 'Cancelled' };
        }
        const result = await api.deleteRoute(metadata.id);
        if (result.success) {
            setDucts(new Map());
            setMetadata(null);
            setIsDirty(false);
            setOriginalState(null);
        }
        return result;
    }, [metadata, api, confirm]);
    // =========================================================================
    // Editor State Actions
    // =========================================================================
    const toggleEditMode = useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);
    const updateMetadata = useCallback((updates) => {
        setMetadata(prev => {
            if (!prev) {
                // Create new metadata if none exists
                return { ...createDefaultMetadata(), ...updates };
            }
            return { ...prev, ...updates };
        });
        setIsDirty(true);
    }, []);
    const markDirty = useCallback(() => {
        setIsDirty(true);
    }, []);
    const cancelEdit = useCallback(() => {
        // Clear all route data and exit edit mode
        setDucts(new Map());
        setMetadata(null);
        setIsDirty(false);
        setOriginalState(null);
        setIsEditing(false);
    }, []);
    // =========================================================================
    // Handle Default Status Change with Bulk Update Offer
    // =========================================================================
    const handleSetDefaultStatus = useCallback(async (status) => {
        setDefaultStatus(status);
        // Only offer bulk update for new routes with "Installed" sections
        if (metadata?.id)
            return;
        const installedSections = lineSections.filter(d => d.properties.BFT_STATUS === 'Installed');
        if (installedSections.length > 0) {
            const confirmed = await confirm({
                title: 'Update Existing Sections',
                description: `Change all ${installedSections.length} "Installed" section(s) to "${status}"?`,
                confirmText: 'Yes',
                cancelText: 'No',
            });
            if (confirmed) {
                setDucts(prev => {
                    const next = new Map(prev);
                    installedSections.forEach(duct => {
                        const dbId = duct.properties.DB_ID;
                        const existing = next.get(dbId);
                        if (existing) {
                            next.set(dbId, {
                                ...existing,
                                properties: {
                                    ...existing.properties,
                                    BFT_STATUS: status,
                                },
                            });
                        }
                    });
                    return next;
                });
                setIsDirty(true);
            }
        }
    }, [metadata, lineSections, confirm]);
    // =========================================================================
    // Context Value
    // =========================================================================
    const value = useMemo(() => ({
        // State
        ducts,
        metadata,
        isEditing,
        isDirty,
        defaultStatus,
        // Computed
        lineSections,
        totalDistance,
        routeInfo,
        // Duct actions
        addDuct,
        removeDuct,
        updateDuctStatus,
        updateDuctNoi,
        clearDucts,
        // Route actions
        loadRoute,
        saveRoute,
        resetRoute,
        copyRoute,
        deleteRoute,
        // Editor actions
        setIsEditing,
        toggleEditMode,
        setDefaultStatus: handleSetDefaultStatus,
        updateMetadata,
        markDirty,
        cancelEdit,
    }), [
        ducts,
        metadata,
        isEditing,
        isDirty,
        defaultStatus,
        lineSections,
        totalDistance,
        routeInfo,
        addDuct,
        removeDuct,
        updateDuctStatus,
        updateDuctNoi,
        clearDucts,
        loadRoute,
        saveRoute,
        resetRoute,
        copyRoute,
        deleteRoute,
        toggleEditMode,
        handleSetDefaultStatus,
        updateMetadata,
        markDirty,
        cancelEdit,
    ]);
    return (_jsx(RouteEditorContext.Provider, { value: value, children: children }));
};
export default RouteEditorContext;
